import { Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { metaOf, type Connection } from "../../lib/connectionsApi";
import { Spin } from "./cxNative";
import type { CxFlows } from "./useCxFlows";

const TITLES = {
  meta: "Connect Meta",
  "meta-wait": "Connect Meta",
  "meta-account": "Which ad account?",
  "meta-pixel": "Which pixel?",
  "email-key": "Connect Smartlead",
  "email-mailboxes": "Sending addresses",
} as const;

const Tick = () => (
  <span className="mo-tick">
    <Icon name="check" />
  </span>
);

/** The connection flows as phone sheets: connect, wait for Facebook, pick the ad account or pixel, the key, the mailboxes. */
export function CxSheetsNative({ f, meta }: { f: CxFlows; meta: Connection | undefined }) {
  const { sheet, sheetErr, sheetBusy } = f;
  const m = metaOf(meta);
  const cancel = (
    <button type="button" className="pn-tbtn" onClick={f.close}>
      Cancel
    </button>
  );
  const err = sheetErr ? <p className="mo-err mo-pad">{sheetErr}</p> : null;
  const main = (label: string, busyLabel: string, onClick: () => void, disabled = false) => (
    <button type="button" className="pn-btn pri wide" disabled={sheetBusy || disabled} onClick={onClick}>
      {sheetBusy ? busyLabel : label}
    </button>
  );

  let body = null;
  let foot = null;
  if (sheet === "meta") {
    body = (
      <>
        <div className="mo-cn">
          <span className="pn-ico">
            <Icon name="bolt" />
          </span>
          <h2>Your Meta ad account</h2>
          <p>Flashadz asks for two things: to manage ads and to read their results. It never posts to your page, and nothing launches without your OK.</p>
        </div>
        {err}
      </>
    );
    foot = main("Continue with Facebook", "Opening…", () => void f.startMeta());
  } else if (sheet === "meta-wait") {
    body = (
      <div className="mo-wait">
        <span className="mo-spin big" aria-hidden="true" />
        <b>Waiting for Facebook</b>
        <span>Sign in and allow access in the Facebook window.</span>
        {f.popupBlocked && f.hasAuthUrl && (
          <button type="button" className="pn-btn soft" onClick={f.reopenPopup}>
            Open the Facebook window
          </button>
        )}
      </div>
    );
  } else if (sheet === "meta-account") {
    body = (
      <>
        <div className="pn-grp" role="radiogroup" aria-label="Ad account">
          {m.adAccounts.map((a) => (
            <button key={a.id} type="button" className="pn-row" role="radio" aria-checked={f.account === a.id} disabled={!a.usable} onClick={() => f.setAccount(a.id)}>
              <span className="m">
                <span className="t">{a.name ?? a.id}</span>
                <span className="s mo-wrap">{a.usable ? [a.id, a.currency, a.timezone].filter(Boolean).join(" · ") : (a.why ?? "Meta does not let ads run on it.")}</span>
              </span>
              {f.account === a.id && <Tick />}
            </button>
          ))}
        </div>
        {err}
      </>
    );
    foot = main("Use this account", "Checking…", () => void f.saveAccount(), !f.account);
  } else if (sheet === "meta-pixel") {
    body = (
      <>
        <p className="mo-sub">The pixel your pages report to.{m.pixels.length ? "" : " Meta listed none for this ad account, so paste its ID from Meta Events Manager, under Data sources."}</p>
        {m.pixels.length ? (
          <div className="pn-grp" role="radiogroup" aria-label="Pixel">
            {m.pixels.map((px) => (
              <button key={px.id} type="button" className="pn-row" role="radio" aria-checked={f.pixel === px.id} onClick={() => f.setPixel(px.id)}>
                <span className="m">
                  <span className="t">{px.name ?? px.id}</span>
                  <span className="s">{[px.id, px.status === "inactive" ? "Meta says it is not active" : null].filter(Boolean).join(" · ")}</span>
                </span>
                {f.pixel === px.id && <Tick />}
              </button>
            ))}
          </div>
        ) : (
          <div className="pn-grp mo-form">
            <div className="pn-irow">
              <label htmlFor="mo-pixel">Pixel ID</label>
              <input id="mo-pixel" value={f.pixel} onChange={(e) => f.setPixel(e.target.value)} inputMode="numeric" autoComplete="off" />
            </div>
          </div>
        )}
        {err}
      </>
    );
    foot = main("Use this pixel", "Checking…", () => void f.savePixel(), !f.pixel.trim());
  } else if (sheet === "email-key") {
    body = (
      <>
        <div className="mo-cn">
          <span className="pn-ico">
            <Icon name="mail" />
          </span>
          <h2>Email sending</h2>
          <p>Paste your Smartlead key. In Smartlead, you find it under Settings.</p>
        </div>
        <form
          className="pn-grp mo-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (f.key.trim()) void f.connectEmail();
          }}
        >
          <div className="pn-irow">
            <label htmlFor="mo-slk">Key</label>
            <input id="mo-slk" type="password" value={f.key} onChange={(e) => f.setKey(e.target.value)} autoComplete="off" placeholder="Paste it here" enterKeyHint="go" />
          </div>
        </form>
        {err}
      </>
    );
    foot = main("Connect", "Checking…", () => void f.connectEmail(), !f.key.trim());
  } else if (sheet === "email-mailboxes") {
    const boxes = f.mailboxes;
    body = (
      <>
        <p className="mo-sub">The addresses to send from. Use a separate domain, so cold email never affects your main one.</p>
        {!boxes && !sheetErr && (
          <div className="pn-grp">
            <div className="pn-row static">
              <Spin />
              <span className="m">
                <span className="t">Reading your Smartlead addresses</span>
              </span>
            </div>
          </div>
        )}
        {!boxes && sheetErr && (
          <>
            {err}
            <div className="mo-pad">
              <button type="button" className="pn-btn soft wide" onClick={() => f.open("email-key")}>
                Paste the key again
              </button>
            </div>
          </>
        )}
        {boxes && boxes.length === 0 && <p className="mo-err mo-pad">Your Smartlead account has no sending address yet. Add one in Smartlead, then check again.</p>}
        {boxes && boxes.length > 0 && (
          <div className="pn-grp">
            {boxes.map((mb) => {
              const on = f.chosen.includes(mb.id);
              return (
                <button key={mb.id} type="button" className="pn-row wrap" role="checkbox" aria-checked={on} onClick={() => f.setChosen((xs) => (on ? xs.filter((x) => x !== mb.id) : [...xs, mb.id]))}>
                  <span className={on ? "mo-chk on" : "mo-chk"}>
                    <Icon name="check" />
                  </span>
                  <span className="m">
                    <span className="t">{mb.fromEmail}</span>
                    <span className="s">
                      {[
                        mb.fromName,
                        mb.smtpOk && mb.imapOk ? "Sends and reads replies" : "Needs reconnecting in Smartlead",
                        mb.maxPerDay !== null ? `up to ${mb.maxPerDay} a day` : "no daily limit set",
                        mb.warmup.reputation !== null ? `trust score ${mb.warmup.reputation}%` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {boxes && err}
      </>
    );
    if (boxes && boxes.length > 0) foot = main("Use these addresses", "Checking…", () => void f.saveMailboxes(), f.chosen.length === 0);
  }

  return (
    <Sheet open={sheet !== null} onClose={f.close} title={sheet ? TITLES[sheet] : undefined} left={cancel} foot={foot} className="mo-sheet">
      {body}
    </Sheet>
  );
}
